export interface BillitPurchaseOrderLink {
  id: number;
  billit_invoice_id: number;
  purchase_order_id: number;
  linked_at: string;
  linked_by_employee_id?: number | null;
  link_comment?: string | null;
  marks_invoice_fully_linked: boolean;
  marks_purchase_order_fully_linked: boolean;
  linked_amount?: number | null;
  unlinked_at?: string | null;
  unlinked_by_employee_id?: number | null;
  unlink_comment?: string | null;
  is_active?: boolean;
  purchase_order?: PurchaseOrder | null;
  linked_by_employee?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  unlinked_by_employee?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  billit_invoice?: {
    id: number;
    billit_order_id: number;
    order_number: string | null;
    order_date: string | null;
    order_status: string | null;
    is_fully_linked: boolean;
    paid?: boolean | null;
    overdue?: boolean | null;
    days_overdue?: number | null;
    to_pay?: number | null;
  } | null;
}

export interface BillitInvoice {
  id: number;
  billit_order_id: number;
  order_number: string | null;
  order_date: string | null;
  expiry_date?: string | null;
  order_status: string | null;
  order_type?: string | null;
  order_direction?: string | null;
  total_excl: number | null;
  total_incl: number | null;
  total_vat: number | null;
  paid: boolean | null;
  overdue: boolean | null;
  days_overdue: number | null;
  currency: string | null;
  customer_json: string | null;
  supplier_json: string | null;
  counterparty_json?: string | null;
  reference?: string | null;
  payment_reference?: string | null;
  reminders_sent?: number | null;
  to_pay?: number | null;
  is_sent?: boolean | null;
  /** App-level: mailed from 3bouw (independent of Billit is_sent) */
  sent?: boolean | null;
  invoiced?: boolean | null;
  assigned_employee_id?: number | null;
  is_fully_linked?: boolean;
  ubl_xml_content?: string | null;
  assigned_employee?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string | null;
  } | null;
  purchase_order_links?: BillitPurchaseOrderLink[];
  billit_invoice_attachments?: BillitInvoiceAttachment[];
  mail_logs?: Array<{
    id: number;
    from?: string | null;
    to?: string | null;
    cc?: string | null;
    bcc?: string | null;
    subject?: string | null;
    body?: string | null;
    attachments?: string | null;
    date_created?: string;
  }>;
  billit_invoice_line_items: Array<{
    id: number;
    quantity: number | null;
    unit_price_excl: number | null;
    description: string | null;
    total_excl: number | null;
    total_vat: number | null;
    vat_percentage: number | null;
    total_incl: number | null;
    description_extended: string | null;
  }>;
  billit_invoice_logs?: Array<{
    id: number;
    remarks: string;
    date_created: string;
    created_by_employee_id: number | null;
    is_from_management: boolean;
    created_by_employee: {
      id: number;
      first_name: string;
      last_name: string;
    } | null;
  }>;
  [key: string]: any;
}

export const getActivePurchaseOrderLinks = (invoice?: BillitInvoice | null) =>
  (invoice?.purchase_order_links || []).filter(
    link => link.is_active !== false && !link.unlinked_at,
  );

export interface BillitInvoiceAttachment {
  id: number;
  billit_invoice_id: number;
  blob_name: string;
  original_name: string;
  content_type?: string | null;
  size?: number | null;
  uploaded_at?: string | null;
  uploaded_by_employee_id?: number | null;
  source_purchase_order_attachment_id?: number | null;
}

export interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  app_user: {
    id: number;
    email: string;
  } | null;
}

export interface ConstructionSite {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  zip_code: number | null;
  site_manager?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
}

export interface PurchaseOrder {
  id: number;
  purchase_order_no: string | null;
  description: string | null;
  creation_date: string | null;
  is_fully_linked?: boolean;
  /** Sum of line items (excl. VAT), when priced lines exist */
  total_excl?: number | null;
  supplier: {
    id: number;
    name: string;
  } | null;
  construction_site?: {
    id: number;
    name: string;
    address: string | null;
    city: string | null;
    zip_code: number | null;
    site_manager?: {
      id: number;
      first_name: string;
      last_name: string;
    } | null;
  } | null;
}
